import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, UserPlus, Check, X, MessageCircle, Heart,
  MessageSquare, Clapperboard, Bell,
} from "lucide-react";
import { socialApi, resolveMedia } from "@/api/social";
import { useSession } from "@/context/SessionContext";
import { Avatar } from "@/components/social/SocialDirectory";

const TABS = [
  { k: "all", label: "All", cat: null },
  { k: "friend", label: "Requests", cat: "friend" },
  { k: "chat", label: "Chat", cat: "chat" },
  { k: "post", label: "Posts", cat: "post" },
  { k: "personal", label: "Personal", cat: "personal" },
];

const ICON = {
  FRIEND_REQ: UserPlus, FRIEND_ACCEPT: Check, CHAT_MSG: MessageCircle,
  POST_LIKE: Heart, POST_COMMENT: MessageSquare,
  REEL_READY: Clapperboard, NEW_REEL: Clapperboard,
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function NotificationsCenter({ onClose, onNavigate }) {
  const { token } = useSession();
  const [tab, setTab] = useState("all");
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState({ friend: 0, chat: 0, post: 0, personal: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const clearedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await socialApi.notifications();
      setRows(d.notifications || []);
      setCats(d.unread_by_category || {});
    } catch {
      toast.error("Could not load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Clear the server-side unread badge once, after the first load (keeps the
  // in-view highlight for this session so the user still sees what's new).
  useEffect(() => {
    if (loading || clearedRef.current) return;
    if ((rows || []).some((n) => n.unread)) {
      clearedRef.current = true;
      socialApi.markRead().catch(() => {});
    }
  }, [loading, rows]);

  const respond = async (n, action) => {
    setBusy(n.id);
    try {
      await socialApi.respond(n.actor_id, action);
      toast.success(action === "accept" ? "Friend added" : "Request declined");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  const openTarget = (n) => {
    if (n.type === "FRIEND_REQ") return; // handled by inline buttons
    if (n.category === "chat") onNavigate?.("chat");
    else if (n.category === "post") onNavigate?.("feed");
    else if (n.category === "personal") onNavigate?.("reels");
    else if (n.category === "friend") onNavigate?.("me");
  };

  const list = tab === "all" ? rows : rows.filter((n) => n.category === tab);

  return (
    <div className="h-full flex flex-col bg-cream relative" data-testid="notifications-center">
      {/* Header */}
      <header className="shrink-0 px-4 pt-3 pb-2 flex items-center gap-3">
        <button onClick={onClose} data-testid="notif-back-btn" className="w-9 h-9 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <ArrowLeft size={16} strokeWidth={2.4} className="text-charcoal" />
        </button>
        <div className="leading-none flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">Activity</p>
          <h1 className="font-serif text-2xl text-charcoal flex items-center gap-2"><Bell size={18} className="text-gold" /> Notifications</h1>
        </div>
      </header>

      {/* Tabs */}
      <div className="shrink-0 px-4 flex gap-1.5 mb-2 overflow-x-auto no-scrollbar" data-testid="notif-tabs">
        {TABS.map((t) => {
          const badge = t.cat ? (cats[t.cat] || 0) : 0;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`notif-tab-${t.k}`}
              className={[
                "relative shrink-0 font-mono text-[10px] font-black uppercase tracking-widest px-3 py-2 rounded-lg border-2 border-charcoal transition-colors duration-150",
                tab === t.k ? "bg-charcoal text-gold" : "bg-white text-charcoal/60",
              ].join(" ")}
            >
              {t.label}
              {badge > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white border-2 border-charcoal grid place-items-center font-mono text-[8px] font-black">{badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-6 space-y-2">
        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 size={26} className="text-gold animate-spin" /></div>
        ) : list.length === 0 ? (
          <p className="text-center font-mono text-xs text-charcoal/40 mt-10" data-testid="notif-empty">Nothing here yet.</p>
        ) : (
          list.map((n) => {
            const Icon = ICON[n.type] || Bell;
            const isReq = n.type === "FRIEND_REQ";
            return (
              <div
                key={n.id}
                data-testid={`notif-${n.id}`}
                onClick={() => openTarget(n)}
                className={[
                  "flex items-center gap-3 border-2 border-charcoal rounded-2xl px-3 py-2.5 shadow-[2px_2px_0px_#1A1A1A] transition-colors",
                  n.unread ? "bg-gold/20" : "bg-white",
                  isReq ? "" : "cursor-pointer active:translate-x-[1px] active:translate-y-[1px]",
                ].join(" ")}
              >
                {n.actor_avatar ? (
                  <Avatar src={n.actor_avatar} name={n.actor_name} size={40} token={token} />
                ) : (
                  <span className="w-10 h-10 rounded-lg border-2 border-charcoal bg-charcoal text-gold grid place-items-center shrink-0"><Icon size={17} /></span>
                )}
                <div className="min-w-0 flex-1 leading-tight">
                  <p className="font-mono text-[11px] text-charcoal leading-snug">
                    {n.actor_name && <span className="font-black">{n.actor_name} </span>}
                    <span>{n.text}</span>
                  </p>
                  <p className="font-mono text-[8px] uppercase tracking-widest text-charcoal/40 mt-0.5 flex items-center gap-1">
                    <Icon size={9} className="text-gold" /> {n.category} · {timeAgo(n.created_at)}
                  </p>
                </div>
                {isReq && (
                  <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => respond(n, "accept")} disabled={busy === n.id} data-testid={`notif-accept-${n.actor_id}`} className="w-8 h-8 grid place-items-center rounded-lg border-2 border-charcoal bg-emerald-500 text-white shadow-[1.5px_1.5px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">
                      {busy === n.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} strokeWidth={2.6} />}
                    </button>
                    <button onClick={() => respond(n, "decline")} disabled={busy === n.id} data-testid={`notif-decline-${n.actor_id}`} className="w-8 h-8 grid place-items-center rounded-lg border-2 border-charcoal bg-white text-charcoal/70 shadow-[1.5px_1.5px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">
                      <X size={14} strokeWidth={2.6} />
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
